import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Play, Pause, X, TrendingUp, Share2, Download, Trophy, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import { groupCompletedTrades } from "../utils/mt5Trades";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MON3 = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const RANGE_OPTS = [
  { id: "month", label: "This Month" },
  { id: "3m", label: "3 Months" },
  { id: "6m", label: "6 Months" },
  { id: "9m", label: "9 Months" },
  { id: "1y", label: "1 Year" },
  { id: "all", label: "All" },
  { id: "custom", label: "Custom" },
];

const money = (n) => `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isLong = (s) => s === 0 || String(s).toLowerCase() === "buy";
const keyOf = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const hm = (ts) => new Date(ts * 1000).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });

/* Mini cumulative-P&L line for a set of trades (ascending by close) */
const MiniPnl = ({ trades, animateTo }) => {
  const pts = useMemo(() => {
    const asc = [...trades].sort((a, b) => (a.ts || 0) - (b.ts || 0));
    let cum = 0; const arr = [{ x: 0, y: 0 }];
    asc.forEach((t, i) => { cum += t.realized; arr.push({ x: i + 1, y: cum }); });
    return arr;
  }, [trades]);
  const n = pts.length - 1 || 1;
  const shown = animateTo == null ? pts : pts.slice(0, animateTo + 2);
  const ys = pts.map((p) => p.y);
  const lo = Math.min(0, ...ys), hi = Math.max(0, ...ys) || 1;
  const W = 640, H = 150, pad = 8;
  const sx = (x) => pad + (x / n) * (W - pad * 2);
  const sy = (y) => H - pad - ((y - lo) / (hi - lo || 1)) * (H - pad * 2);
  const line = shown.map((p, i) => `${i === 0 ? "M" : "L"}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(" ");
  const last = shown[shown.length - 1] || { y: 0 };
  const up = last.y >= 0;
  const col = up ? "#34d399" : "#f87171";
  const area = `${line} L${sx((shown[shown.length - 1] || pts[0]).x).toFixed(1)},${sy(lo).toFixed(1)} L${sx(0)},${sy(lo).toFixed(1)} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[150px]" preserveAspectRatio="none">
      <defs><linearGradient id="dv-grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={col} stopOpacity="0.28" /><stop offset="100%" stopColor={col} stopOpacity="0" /></linearGradient></defs>
      <line x1={pad} y1={sy(0)} x2={W - pad} y2={sy(0)} stroke="rgba(255,255,255,0.12)" strokeDasharray="4 4" />
      <path d={area} fill="url(#dv-grad)" />
      <path d={line} fill="none" stroke={col} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

/* Animated day / trade replay modal */
const ReplayModal = ({ title, trades, onClose }) => {
  const asc = useMemo(() => [...trades].sort((a, b) => (a.ts || 0) - (b.ts || 0)), [trades]);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);
  useEffect(() => {
    if (!playing) return;
    timer.current = setInterval(() => {
      setStep((s) => { if (s >= asc.length - 1) { setPlaying(false); return s; } return s + 1; });
    }, 850);
    return () => clearInterval(timer.current);
  }, [playing, asc.length]);
  const cum = asc.slice(0, step + 1).reduce((a, t) => a + t.realized, 0);
  const cur = asc[step];
  return (
    <div className="ws-broker-modal-overlay" onClick={onClose} data-testid="dayview-replay-modal">
      <div className="ws-broker-modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <button className="ws-broker-modal-close" onClick={onClose} data-testid="replay-close"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-[color:var(--ws-cyan)]" /><h3 className="ws-heading text-[16px] font-bold">Replay · {title}</h3></div>
        <p className="text-[12px] text-[color:var(--ws-muted)] mb-3">Trade {step + 1} / {asc.length} · Running P&L <span className={cum >= 0 ? "ws-up" : "ws-down"}>{money(cum)}</span></p>
        <div className="ws-price-cell p-3 rounded-xl"><MiniPnl trades={asc} animateTo={step} /></div>
        {cur && (
          <div className="flex items-center justify-between mt-3 text-[12.5px]">
            <span className="font-semibold text-white">{cur.symbol}</span>
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${isLong(cur.side) ? "bg-[rgba(52,211,153,0.15)] text-[color:var(--ws-green)]" : "bg-[rgba(248,113,113,0.15)] text-[color:var(--ws-red)]"}`}>{isLong(cur.side) ? "LONG" : "SHORT"}</span>
            <span className="text-[color:var(--ws-muted)]">{hm(cur.ts)}</span>
            <span className={cur.realized >= 0 ? "ws-up font-semibold" : "ws-down font-semibold"}>{money(cur.realized)}</span>
          </div>
        )}
        <div className="flex items-center gap-2 mt-4">
          <button onClick={() => { setStep(0); setPlaying(true); }} className="ws-btn-ghost px-3 py-2 rounded-lg text-[12px] font-semibold flex-1" data-testid="replay-restart">Restart</button>
          <button onClick={() => setPlaying((p) => !p)} className="ws-btn-primary px-4 py-2 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5" data-testid="replay-toggle">
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />} {playing ? "Pause" : "Play"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* Branded, downloadable PnL calendar share card — renders one calendar per month in the range */
const monthNetOf = (byDay, m) => {
  let sum = 0;
  Object.keys(byDay).forEach((k) => {
    const [y, mo] = k.split("-").map(Number);
    if (y === m.getFullYear() && mo === m.getMonth()) sum += byDay[k].reduce((a, t) => a + t.realized, 0);
  });
  return sum;
};

const compactMoney = (n) => `${n >= 0 ? "+" : "-"}$${Math.abs(n) >= 1000 ? (Math.abs(n) / 1000).toFixed(1) + "k" : Math.abs(n).toFixed(0)}`;

const MonthGrid = ({ month, byDay, detailed }) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [...Array(startPad).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const net = monthNetOf(byDay, month);
  return (
    <div className={detailed ? "" : "ws-share-mini"}>
      <div className="ws-share-mini-head">
        <span className="ws-share-mini-title">{MON3[month.getMonth()]} {month.getFullYear()}</span>
        <span className={`ws-share-mini-net ${net >= 0 ? "ws-share-pos" : "ws-share-neg"}`}>{compactMoney(net)}</span>
      </div>
      <div className={detailed ? "ws-share-dow" : "ws-share-mini-dow"}>{DOW.map((d) => <span key={d}>{detailed ? d : d[0]}</span>)}</div>
      <div className={detailed ? "ws-share-grid" : "ws-share-mini-grid"}>
        {cells.map((day, i) => {
          if (day == null) return <span key={i} className={`${detailed ? "ws-share-cell ws-share-empty" : "ws-share-mcell ws-share-mempty"}`} />;
          const k = keyOf(new Date(month.getFullYear(), month.getMonth(), day));
          const list = byDay[k];
          const dn = list ? list.reduce((a, t) => a + t.realized, 0) : null;
          const up = dn != null && dn >= 0;
          if (detailed) {
            const cls = dn == null ? "" : up ? "ws-share-cell-up" : "ws-share-cell-down";
            return (
              <span key={i} className={`ws-share-cell ${cls}`}>
                <em>{day}</em>
                {dn != null && <b>{compactMoney(dn)}</b>}
              </span>
            );
          }
          const cls = dn == null ? "" : up ? "ws-share-mcell-up" : "ws-share-mcell-down";
          return <span key={i} className={`ws-share-mcell ${cls}`}>{day}</span>;
        })}
      </div>
    </div>
  );
};

const ShareCard = ({ months, byDay, rangeLabel, stats, onClose }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const single = months.length === 1;
  const cols = single ? 1 : months.length <= 4 ? 2 : 3;
  const m0 = months[0];

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2.6, cacheBust: true, skipFonts: true, backgroundColor: "#05070d" });
      const a = document.createElement("a");
      const span = single ? `${MON3[m0.getMonth()].toLowerCase()}-${m0.getFullYear()}` : `${months.length}mo`;
      a.download = `wealth-secret-pnl-${span}.png`;
      a.href = dataUrl;
      a.click();
    } catch (e) { console.error("share export failed", e); }
    setBusy(false);
  };

  return (
    <div className="ws-broker-modal-overlay" onClick={onClose} data-testid="dayview-share-modal">
      <div className="ws-share-wrap" onClick={(e) => e.stopPropagation()}>
        <button className="ws-broker-modal-close" onClick={onClose} data-testid="share-close"><X className="w-4 h-4" /></button>

        {/* The exportable card */}
        <div className="ws-share-card" ref={cardRef} data-testid="share-card">
          <div className="ws-share-glow" aria-hidden />
          <div className="ws-share-head">
            <div className="ws-share-brand">
              <img src="/wsj-logo.webp" alt="Wealth Secret Journal" className="ws-share-logo" crossOrigin="anonymous" />
              <div>
                <div className="ws-share-brand-name">WEALTH SECRET</div>
                <div className="ws-share-brand-sub">JOURNAL</div>
              </div>
            </div>
            <div className="ws-share-period">{rangeLabel}</div>
          </div>

          <div className="ws-share-hero">
            <div className="ws-share-hero-label">Net P&amp;L</div>
            <div className={`ws-share-hero-value ${stats.net >= 0 ? "ws-share-pos" : "ws-share-neg"}`}>{money(stats.net)}</div>
          </div>

          <div className="ws-share-stats">
            <div className="ws-share-stat"><span>Win Rate</span><b className={stats.winRate >= 50 ? "ws-share-pos" : ""}>{`${stats.winRate}%`}</b></div>
            <div className="ws-share-stat"><span>Trading Days</span><b>{stats.days}</b></div>
            <div className="ws-share-stat"><span>Trades</span><b>{stats.count}</b></div>
            <div className="ws-share-stat"><span>Best Day</span><b className="ws-share-pos">{stats.best != null ? money(stats.best) : "—"}</b></div>
          </div>

          <div className="ws-share-cal-title">{single ? `${MONTHS[m0.getMonth()]} ${m0.getFullYear()} · daily P&L` : `Daily P&L · ${months.length} months`}</div>
          {single ? (
            <MonthGrid month={m0} byDay={byDay} detailed />
          ) : (
            <div className="ws-share-months" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
              {months.map((m, i) => <MonthGrid key={i} month={m} byDay={byDay} detailed={false} />)}
            </div>
          )}

          <div className="ws-share-foot">
            <span className="ws-share-foot-tag">Trade with discipline.</span>
            <span className="ws-share-foot-social">@wealthsecretofficial · youtube.com/@wealthsecret23</span>
          </div>
        </div>

        <button onClick={download} disabled={busy} className="ws-btn-primary w-full mt-3 py-2.5 rounded-lg text-[13px] font-semibold inline-flex items-center justify-center gap-2" data-testid="share-download">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {busy ? "Rendering…" : "Download share card"}
        </button>
      </div>
    </div>
  );
};

const DayView = () => {
  const [trades, setTrades] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selKey, setSelKey] = useState(null);
  const [replay, setReplay] = useState(null);
  const [range, setRange] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/mt5/trades`, { credentials: "include" });
        const d = await res.json();
        setTrades(groupCompletedTrades(d.trades || d || []));
      } catch { setTrades([]); }
    })();
  }, []);

  // group trades by local close-day
  const byDay = useMemo(() => {
    const m = {};
    (trades || []).forEach((t) => {
      if (!t.ts) return;
      const d = new Date(t.ts * 1000);
      const k = keyOf(d);
      (m[k] = m[k] || []).push(t);
    });
    return m;
  }, [trades]);

  // Period range (This Month / 3M / 6M / 9M / 1Y / All / Custom) → aggregate stats
  const rangeStats = useMemo(() => {
    const now = new Date();
    let start = null, end = null;
    if (range === "custom") {
      start = customFrom ? new Date(customFrom + "T00:00:00") : null;
      end = customTo ? new Date(customTo + "T23:59:59") : null;
    } else if (range === "month") {
      start = new Date(now.getFullYear(), now.getMonth(), 1); end = now;
    } else if (range !== "all") {
      const m = { "3m": 3, "6m": 6, "9m": 9, "1y": 12 }[range] || 0;
      start = new Date(now); start.setMonth(start.getMonth() - m); start.setHours(0, 0, 0, 0); end = now;
    }
    const list = (trades || []).filter((t) => {
      if (!t.ts) return false;
      const d = new Date(t.ts * 1000);
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
    const net = list.reduce((a, t) => a + t.realized, 0);
    const wins = list.filter((t) => t.realized > 0).length;
    const losses = list.filter((t) => t.realized < 0).length;
    const decided = wins + losses;
    const dayMap = {};
    list.forEach((t) => { const k = keyOf(new Date(t.ts * 1000)); dayMap[k] = (dayMap[k] || 0) + t.realized; });
    const dayNets = Object.values(dayMap);
    return {
      net, count: list.length, wins, losses,
      winRate: decided ? Math.round((wins / decided) * 100) : 0,
      days: Object.keys(dayMap).length,
      best: dayNets.length ? Math.max(...dayNets) : null,
      worst: dayNets.length ? Math.min(...dayNets) : null,
    };
  }, [trades, range, customFrom, customTo]);

  const rangeLabel = range === "custom"
    ? (customFrom && customTo ? `${customFrom} → ${customTo}` : "Custom range")
    : (RANGE_OPTS.find((r) => r.id === range)?.label || "This Month");

  // Months to render on the share card — one calendar per month in the selected range
  const shareMonths = useMemo(() => {
    const now = new Date();
    const mk = (y, m) => new Date(y, m, 1);
    const build = (count, end) => Array.from({ length: count }, (_, i) => new Date(end.getFullYear(), end.getMonth() - (count - 1 - i), 1));
    const span = (s, e) => { const arr = []; let d = mk(s.getFullYear(), s.getMonth()); const endM = mk(e.getFullYear(), e.getMonth()); while (d <= endM && arr.length < 24) { arr.push(new Date(d)); d = new Date(d.getFullYear(), d.getMonth() + 1, 1); } return arr; };
    if (range === "month") return [mk(now.getFullYear(), now.getMonth())];
    if (range === "3m") return build(3, now);
    if (range === "6m") return build(6, now);
    if (range === "9m") return build(9, now);
    if (range === "1y") return build(12, now);
    if (range === "custom") {
      if (!customFrom || !customTo) return [mk(now.getFullYear(), now.getMonth())];
      return span(new Date(customFrom + "T00:00:00"), new Date(customTo + "T00:00:00"));
    }
    // all → earliest trade month through current month
    const tss = (trades || []).map((t) => t.ts).filter(Boolean);
    if (!tss.length) return [mk(now.getFullYear(), now.getMonth())];
    return span(new Date(Math.min(...tss) * 1000), now);
  }, [range, customFrom, customTo, trades]);

  // default selected = most recent day with trades
  useEffect(() => {
    if (!trades || selKey) return;
    const keys = Object.keys(byDay);
    if (!keys.length) return;
    const latest = keys.map((k) => byDay[k][0].ts).reduce((a, b) => Math.max(a, b), 0);
    const d = new Date(latest * 1000);
    setSelKey(keyOf(d));
    setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
  }, [trades, byDay, selKey]);

  const dayStats = (list = []) => {
    const net = list.reduce((a, t) => a + t.realized, 0);
    const gross = list.reduce((a, t) => a + (t.grossProfit || 0), 0);
    const comm = list.reduce((a, t) => a + (t.costs || 0), 0);
    const wins = list.filter((t) => t.realized > 0);
    const losses = list.filter((t) => t.realized < 0);
    const gw = wins.reduce((a, t) => a + t.realized, 0);
    const gl = Math.abs(losses.reduce((a, t) => a + t.realized, 0));
    const vol = list.reduce((a, t) => a + (t.volume || 0), 0);
    const decided = wins.length + losses.length;
    return { net, gross, comm, wins: wins.length, losses: losses.length, winRate: decided ? Math.round(wins.length / decided * 100) : 0, pf: gl > 0 ? Math.round(gw / gl * 100) / 100 : (gw > 0 ? 999 : 0), vol: Math.round(vol * 100) / 100, count: list.length };
  };

  // calendar cells
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [...Array(startPad).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const selList = selKey ? (byDay[selKey] || []) : [];
  const selDate = selKey ? new Date(...selKey.split("-").map((n, i) => (i === 1 ? +n : +n))) : null;
  const st = dayStats(selList);

  const changeMonth = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  if (trades === null) return <div className="ws-panel p-10 text-center text-[color:var(--ws-muted)]" data-testid="dayview-loading">Loading day view…</div>;
  if (!Object.keys(byDay).length) return <div className="ws-panel p-10 text-center" data-testid="dayview-empty"><CalendarDays className="w-8 h-8 mx-auto mb-3 text-[color:var(--ws-cyan)]" /><p className="text-[color:var(--ws-muted)] text-sm">No trades yet. Connect MT5 and sync to review your days.</p></div>;

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-5" data-testid="dayview-root">
      {/* Left: selected day */}
      <div className="ws-panel p-6">
        {selKey && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="ws-heading text-[16px] font-bold" data-testid="dayview-selected-date">
                  {selDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                </div>
                <div className={`text-[15px] font-bold ${st.net >= 0 ? "ws-up" : "ws-down"}`} data-testid="dayview-net">Net P&L {money(st.net)}</div>
              </div>
              <button onClick={() => setReplay({ title: "Full day", list: selList })} className="ws-btn-ghost px-3 py-2 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5" data-testid="dayview-replay-day">
                <Play className="w-3.5 h-3.5" /> Replay
              </button>
            </div>

            <div className="ws-price-cell p-3 rounded-xl mb-5"><MiniPnl trades={selList} /></div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              {[
                ["Total Trades", st.count, ""],
                ["Win Rate", `${st.winRate}%`, st.winRate >= 50 ? "ws-up" : ""],
                ["Gross P&L", money(st.gross), st.gross >= 0 ? "ws-up" : "ws-down"],
                ["Winners / Losers", `${st.wins} / ${st.losses}`, ""],
                ["Volume", st.vol.toFixed(2), ""],
                ["Profit Factor", st.pf >= 999 ? "∞" : st.pf.toFixed(2), st.pf >= 1.3 ? "ws-up" : ""],
                ["Commissions", money(st.comm), ""],
              ].map(([l, v, c]) => (
                <div key={l}>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--ws-muted)]">{l}</div>
                  <div className={`ws-heading text-[18px] font-bold mt-0.5 ${c || "text-white"}`}>{v}</div>
                </div>
              ))}
            </div>

            {/* trades list */}
            <div className="grid grid-cols-[80px_1fr_70px_60px_1fr_50px] gap-2 text-[10px] uppercase tracking-wide text-[color:var(--ws-muted)] px-1 mb-1">
              <span>Open</span><span>Symbol</span><span>Side</span><span>Qty</span><span className="text-right">Net P&L</span><span className="text-right">Replay</span>
            </div>
            <div className="space-y-1 max-h-[260px] overflow-y-auto" data-testid="dayview-trades">
              {[...selList].sort((a, b) => (a.openTs || a.ts) - (b.openTs || b.ts)).map((t) => (
                <div key={t.key} className="grid grid-cols-[80px_1fr_70px_60px_1fr_50px] gap-2 items-center ws-price-cell px-2 py-2.5 rounded-lg text-[12.5px]">
                  <span className="text-[color:var(--ws-muted)]">{hm(t.openTs || t.ts)}</span>
                  <span className="font-semibold text-white">{t.symbol}</span>
                  <span><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isLong(t.side) ? "bg-[rgba(52,211,153,0.15)] text-[color:var(--ws-green)]" : "bg-[rgba(248,113,113,0.15)] text-[color:var(--ws-red)]"}`}>{isLong(t.side) ? "LONG" : "SHORT"}</span></span>
                  <span className="text-white/80">{t.volume ?? "—"}</span>
                  <span className={`text-right font-semibold ${t.realized >= 0 ? "ws-up" : "ws-down"}`}>{money(t.realized)}</span>
                  <span className="text-right">
                    <button onClick={() => setReplay({ title: `${t.symbol} · ${hm(t.openTs || t.ts)}`, list: [t] })} className="text-[color:var(--ws-cyan)] hover:opacity-80" data-testid={`dayview-replay-trade-${t.key}`}><Play className="w-3.5 h-3.5 inline" /></button>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Right: calendar */}
      <div className="ws-panel p-5 self-start" data-testid="dayview-calendar">
        <div className="flex items-center justify-between mb-3">
          <h3 className="ws-heading text-[15px] font-bold">{MONTHS[month.getMonth()]} {month.getFullYear()}</h3>
          <div className="flex gap-1 items-center">
            <button onClick={() => setShareOpen(true)} className="ws-btn-ghost p-1.5 rounded-md" title="Share P&L calendar" data-testid="dayview-share-btn"><Share2 className="w-4 h-4" /></button>
            <button onClick={() => changeMonth(-1)} className="ws-btn-ghost p-1.5 rounded-md" data-testid="dayview-prev-month"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => changeMonth(1)} className="ws-btn-ghost p-1.5 rounded-md" data-testid="dayview-next-month"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Period range selector */}
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="dayview-range">
          {RANGE_OPTS.map((r) => (
            <button key={r.id} onClick={() => setRange(r.id)} data-testid={`dayview-range-${r.id}`}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${range === r.id ? "bg-[color:var(--ws-blue)] text-white" : "ws-btn-ghost text-[color:var(--ws-muted)]"}`}>
              {r.label}
            </button>
          ))}
        </div>
        {range === "custom" && (
          <div className="flex items-center gap-2 mb-3" data-testid="dayview-custom-inputs">
            <input type="date" value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)}
              className="flex-1 bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-md text-[11px] text-white px-2 py-1.5 outline-none" data-testid="dayview-custom-from" />
            <span className="text-[color:var(--ws-muted)] text-[11px]">→</span>
            <input type="date" value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)}
              className="flex-1 bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-md text-[11px] text-white px-2 py-1.5 outline-none" data-testid="dayview-custom-to" />
          </div>
        )}

        {/* Period summary */}
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="dayview-period-summary">
          <div className="ws-price-cell p-2 rounded-lg text-center">
            <div className="text-[9px] uppercase tracking-wide text-[color:var(--ws-muted)]">Net P&L</div>
            <div className={`ws-heading text-[13px] font-bold mt-0.5 ${rangeStats.net >= 0 ? "ws-up" : "ws-down"}`} data-testid="dayview-period-net">{money(rangeStats.net)}</div>
          </div>
          <div className="ws-price-cell p-2 rounded-lg text-center">
            <div className="text-[9px] uppercase tracking-wide text-[color:var(--ws-muted)]">Win Rate</div>
            <div className={`ws-heading text-[13px] font-bold mt-0.5 ${rangeStats.winRate >= 50 ? "ws-up" : "text-white"}`}>{`${rangeStats.winRate}%`}</div>
          </div>
          <div className="ws-price-cell p-2 rounded-lg text-center">
            <div className="text-[9px] uppercase tracking-wide text-[color:var(--ws-muted)]">Trades</div>
            <div className="ws-heading text-[13px] font-bold mt-0.5 text-white">{rangeStats.count}</div>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-[color:var(--ws-muted)] mb-1">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (day == null) return <span key={i} />;
            const dObj = new Date(month.getFullYear(), month.getMonth(), day);
            const k = keyOf(dObj);
            const list = byDay[k];
            const net = list ? list.reduce((a, t) => a + t.realized, 0) : null;
            const sel = k === selKey;
            const cls = net == null ? "text-[color:var(--ws-muted)]" : net >= 0 ? "ws-dv-day-up" : "ws-dv-day-down";
            return (
              <button key={i} onClick={() => list && setSelKey(k)} disabled={!list}
                className={`ws-dv-day ${cls} ${sel ? "ws-dv-day-sel" : ""}`} data-testid={`dayview-day-${day}`}>
                {day}
                {net != null && <span className="ws-dv-dot" />}
              </button>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t border-[color:var(--ws-border)] flex items-center justify-between text-[11px]">
          <span className="text-[color:var(--ws-muted)]">Trading days · {rangeLabel}</span>
          <span className="font-bold text-white" data-testid="dayview-period-days">{rangeStats.days}</span>
        </div>
        <button onClick={() => setShareOpen(true)} className="ws-btn-primary w-full mt-3 py-2 rounded-lg text-[12px] font-semibold inline-flex items-center justify-center gap-1.5" data-testid="dayview-share-cta">
          <Share2 className="w-3.5 h-3.5" /> Share P&L Card
        </button>
      </div>

      {shareOpen && <ShareCard months={shareMonths} byDay={byDay} rangeLabel={rangeLabel} stats={rangeStats} onClose={() => setShareOpen(false)} />}

      {replay && <ReplayModal title={replay.title} trades={replay.list} onClose={() => setReplay(null)} />}
    </div>
  );
};

export default DayView;
